import { useMemo } from 'react';
import { DataTable } from '@/components/common/DataTable';
import { ISnowfallItem } from '@/api/resort/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { SnowUnit } from '@/components/blocks/Unit';
import { Heading } from '@/components/ui/heading';
import { parseDate } from '@/util/formatDate';

// Annual snowfall table for a single resort. Columns: season label
// (e.g. "2024 — 2025" or the current season as "2024 — to date") /
// total snowfall / snow days / avg base depth / max base depth (md+) /
// biggest snowfall (md+). A synthetic Average row gets appended at
// the bottom, bolded.

type KeyAverage =
  | 'totalSnow'
  | 'snowDays'
  | 'baseDepth'
  | 'summitDepth'
  | 'maxBaseDepth'
  | 'biggestSnowfall';

const computeAverages = (arr: Array<ISnowfallItem>) => (property: KeyAverage) => {
  const sum = arr.reduce((prev, item) => prev + item[property], 0);
  return Math.ceil(sum / arr.length);
};

export const Annual = ({
  infoAnnual,
  resortName,
}: {
  infoAnnual: Array<ISnowfallItem>;
  resortName: string;
}) => {
  const { t } = useOTSTranslation('common');
  const setAverageInstance = useMemo(() => computeAverages(infoAnnual), [infoAnnual]);

  const average = useMemo(
    () => ({
      date: t('resort.historicalSnowfall.average'),
      totalSnow: setAverageInstance('totalSnow'),
      snowDays: setAverageInstance('snowDays'),
      baseDepth: setAverageInstance('baseDepth'),
      summitDepth: setAverageInstance('summitDepth'),
      maxBaseDepth: setAverageInstance('maxBaseDepth'),
      biggestSnowfall: setAverageInstance('biggestSnowfall'),
    }),
    [setAverageInstance, t],
  );

  const rows = useMemo(() => [...infoAnnual, average], [infoAnnual, average]);
  // The last "real" season in the API is the in-progress one (no
  // following season exists in the list). Match the prior component's
  // "{year} — to date" suffix for it.
  const lastSeasonDate = infoAnnual[infoAnnual.length - 1]?.date;

  const formatRowLabel = (date: string, isAverage: boolean) => {
    if (isAverage) return t('resort.historicalSnowfall.average');
    const { year } = parseDate(date);
    if (date === lastSeasonDate) {
      return `${year} — ${t('resort.historicalSnowfall.toDate')}`;
    }
    return `${year} — ${year + 1}`;
  };

  return (
    <section className="mb-10">
      <Heading
        level={2}
        className="mb-4"
      >
        {t('resort.historicalSnowfall.annualTabTitle' as never, { resort: resortName })}
      </Heading>
      <DataTable minWidth={520}>
        <DataTable.Head>
          <DataTable.HeadRow>
            <DataTable.HeadCell />
            <DataTable.HeadCell align="right">
              {t('resort.historicalSnowfall.totalSnowfall')}
            </DataTable.HeadCell>
            <DataTable.HeadCell align="right">
              {t('resort.historicalSnowfall.snowfallDay')}
            </DataTable.HeadCell>
            <DataTable.HeadCell align="right">
              {t('resort.historicalSnowfall.avgBaseDepth')}
            </DataTable.HeadCell>
            <DataTable.HeadCell align="right" className="hidden md:table-cell">
              {t('resort.historicalSnowfall.maxBaseDepth')}
            </DataTable.HeadCell>
            <DataTable.HeadCell align="right" className="hidden md:table-cell">
              {t('resort.historicalSnowfall.biggestSnowfall')}
            </DataTable.HeadCell>
          </DataTable.HeadRow>
        </DataTable.Head>
        <DataTable.Body>
          {rows.map((item, i) => {
            const isAverage = item.date === average.date;
            const pluralDays = item.snowDays !== 1;
            return (
              <DataTable.Row
                key={item.date}
                zebra={i % 2 === 0}
                className={isAverage ? 'bg-sky-100/60 font-semibold' : undefined}
              >
                <DataTable.RowHeader className="whitespace-nowrap">
                  {formatRowLabel(item.date, isAverage)}
                </DataTable.RowHeader>
                <DataTable.Cell align="right">
                  <SnowUnit values={item.totalSnow} />
                </DataTable.Cell>
                <DataTable.Cell align="right">
                  {t(pluralDays ? 'common.timeDay_plural' : 'common.timeDay', {
                    count: item.snowDays,
                  })}
                </DataTable.Cell>
                <DataTable.Cell align="right">
                  <SnowUnit values={item.baseDepth} />
                </DataTable.Cell>
                <DataTable.Cell align="right" className="hidden md:table-cell">
                  <SnowUnit values={item.maxBaseDepth} />
                </DataTable.Cell>
                <DataTable.Cell align="right" className="hidden md:table-cell">
                  <SnowUnit values={item.biggestSnowfall} />
                </DataTable.Cell>
              </DataTable.Row>
            );
          })}
        </DataTable.Body>
      </DataTable>
    </section>
  );
};
